import React from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { AddRestaurantParams } from '@/api/lib/restaurant';

import { WriteSwornDeclarationForm } from '../WriteSwornDeclarationForm';
import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { TextInput } from '@/components/forms/base/TextInput';
import { Form } from '@/components/forms/core/Form';
import { AutoCompleteRestaurantInput } from '@/components/forms/custom/AutocompleteRestaurantInput';
import { Button } from '@/components/molecules/Button';

import { AddRestaurantSchemaType, validationSchema } from './schema';
import { useAskClaimRestaurantMutation } from '@/api/hooks/useClaim';

export interface ClaimRestaurantFormProps {
  initialData?: AddRestaurantSchemaType;
  restaurant?: AddRestaurantParams;
  userEmail?: string;
}

export const ClaimRestaurantForm = ({ initialData, userEmail, restaurant }: ClaimRestaurantFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { isLoading: isAskingClaimRestaurant, mutateAsync: askClaimMutation } = useAskClaimRestaurantMutation({});

  const handleSubmit = async (data: AddRestaurantParams) => {
    if (data.isPlaceAlreadyRegistered) {
      toast.error(t('proposeRestaurant.restaurantOwnershipMessage'), {
        position: 'bottom-right'
      });
    } else {
      await askClaimMutation({
        restaurantId: restaurant?.id || '',
        params: {
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          suppliers: data.suppliers?.filter((supplier): supplier is string => supplier !== null && supplier !== undefined) || [],
          company: data.company || '',
          email: data.email || '',
          phoneNumber: data.phoneNumberOwner || ''
        }
      });
      navigate(`/claim?email=${data.email}&restaurant=${data.autocompleteName}&info=checkEmail`);
    }
  };

  return (
    <div className="gap-4 flex flex-col w-full">
      <Form
        data-testid="restaurant-form"
        schema={validationSchema(t)}
        onSubmit={handleSubmit}
        onlyTouchedFields={false}
        defaultValues={
          initialData && {
            ...initialData,
            suppliers: initialData.suppliers?.filter((supplier): supplier is string => supplier !== null && supplier !== undefined),
            company: initialData.company
          }
        }
        className="flex flex-col gap-5"
        resetOnSubmit={!initialData}
        render={({ formState, watch, setValue }) => {
          const restaurantName = watch('autocompleteName');
          const restaurantAddress = restaurantName ? restaurantName.split(',')[0] : '';
          const firstName = watch('firstName');
          const lastName = watch('lastName');

          React.useEffect(() => {
            if (firstName) {
              setValue('firstName', firstName);
            }
            if (lastName) {
              setValue('lastName', lastName);
            }
            if (restaurantName) {
              setValue('restaurantName', restaurantName);
            }
            if (restaurantAddress) {
              setValue('address', restaurantAddress);
            }
          }, [restaurantName, restaurantAddress, firstName, lastName, setValue]);

          return (
            <>
              {!isAskingClaimRestaurant ? (
                <>
                  <AutoCompleteRestaurantInput
                    searchInputId="autocompleteName"
                    searchPlaceId="placeId"
                    label={t('form.restaurantAndCity')}
                    weight="bold"
                    disabled={!!initialData?.autocompleteName}
                    placeholder={t('form.restaurantAndCityPlaceholder')}
                    required
                  />
                  {initialData?.autocompleteName && (
                    <div className="text-gray-500 -mt-5 text-sm">La proposition de fiche en cours est uniquement applicable au restaurant sélectionné.</div>
                  )}

                  <span className="bg-gray-100 p-1 px-6 w-fit mx-auto rounded-lg">
                    <Typography transform="uppercase" color="gray">
                      {t('form.restaurantInformation')}
                    </Typography>
                  </span>

                  <div className="flex flex-1 flex-row gap-5 md:gap-12">
                    <div className="flex flex-1 flex-col gap-5">
                      <TextInput id="phoneNumber" label={t('form.yourPhone')} placeholder={t('form.yourPhonePlaceholder')} required />
                    </div>
                    <div className="flex flex-1 flex-col gap-5">
                      <TextInput
                        id="email"
                        label={t('form.yourEmailAddress')}
                        defaultValue={userEmail}
                        placeholder={t('form.yourEmailAddressPlaceholder')}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-row gap-5 md:gap-12">
                    <div className="flex flex-1 flex-col gap-5">
                      <TextInput id="firstName" label={t('form.firstName')} placeholder={t('form.firstName')} required />
                    </div>
                    <div className="flex flex-1 flex-col gap-5">
                      <TextInput id="lastName" label={t('form.lastName')} placeholder={t('form.lastName')} required />
                    </div>
                  </div>
                  <div className="flex justify-center mt-4 md:mt-14 mb-2">
                    <span className="bg-gray-100 p-1 px-6 rounded-lg">
                      <Typography transform="uppercase" color="gray">
                        {t('form.declaration')}
                      </Typography>
                    </span>
                  </div>

                  <WriteSwornDeclarationForm watch={watch} showRepresentativeRestaurantAddress={false} />

                  <div className="flex items-center gap-1 mt-5">
                    <input type="checkbox" id="acceptTerms" className="mr-2 rounded" required />
                    <Typography variant="extraSmall" color="gray">
                      {t('swornDeclaration.acceptTerms')}
                    </Typography>
                  </div>
                </>
              ) : (
                <>
                  <Spinner big />
                  <Typography variant="h4" color="gray">
                    Envoi de vos données en cours. Veuillez patienter...
                  </Typography>
                </>
              )}
              {!isAskingClaimRestaurant && (
                <Button type="submit" fullWidth color="primary" label="Envoyer" loading={isAskingClaimRestaurant} disabled={!formState.isDirty} />
              )}
            </>
          );
        }}
      />
    </div>
  );
};
